
import { format, addDays, subDays } from "date-fns";

export type StatusType = 'scheduled' | 'pending' | 'completed' | 'published' | 'draft';

export type SocialLinks = {
  twitter?: string;
  linkedin?: string;
  website?: string;
  youtube?: string;
  instagram?: string;
};

export interface Guest {
  id: string;
  name: string;
  imageUrl: string;
  title: string;
  company: string;
  email: string;
  phone?: string;
  bio: string;
  topics: string[];
  tags?: string[];
  status: StatusType;
  scheduledDate?: string;
  recordingUrl?: string;
  notes?: string;
  socialLinks?: SocialLinks;
}

export interface Episode {
  id: string;
  title: string;
  description: string;
  guestId: string;
  recordingDate: string;
  publishDate?: string;
  duration?: string;
  status: StatusType;
  thumbnailUrl: string;
  audioUrl?: string;
  videoUrl?: string;
  showNotes?: string;
  tags: string[];
  listenCount?: number;
  episodeNumber?: number;
}

export interface Statistics {
  totalGuests: number;
  upcomingEpisodes: number;
  publishedEpisodes: number;
  reachStats: {
    totalListens: number;
    avgListensPerEpisode: number;
    growth: number;
  };
}

export const getStatusColor = (status: StatusType): string => {
  switch (status) {
    case 'scheduled':
      return 'bg-status-scheduled';
    case 'pending':
      return 'bg-status-pending';
    case 'completed':
      return 'bg-status-completed';
    case 'published':
      return 'bg-status-published';
    case 'draft':
      return 'bg-status-draft';
    default:
      return 'bg-muted';
  }
};

export const getStatusLabel = (status: StatusType): string => {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'pending':
      return 'Pending';
    case 'completed':
      return 'Completed';
    case 'published':
      return 'Published';
    case 'draft':
      return 'Draft';
    default:
      return status;
  }
};

export const guests: Guest[] = [
  {
    id: "g1",
    name: "Dr. Sarah Johnson",
    imageUrl: "https://randomuser.me/api/portraits/women/1.jpg",
    title: "AI Research Scientist",
    company: "DeepMind",
    email: "sarah.johnson@example.com",
    phone: "+1 (555) 123-4567",
    bio: "Dr. Sarah Johnson is a leading AI researcher specializing in reinforcement learning and neural networks. She has published over 50 papers and holds multiple patents in machine learning technologies.",
    topics: ["Artificial Intelligence", "Machine Learning", "Neural Networks"],
    tags: ["AI", "Tech", "Research"],
    status: "scheduled",
    scheduledDate: format(addDays(new Date(), 3), "yyyy-MM-dd'T'HH:mm:ss"),
    notes: "Interested in discussing recent breakthroughs in reinforcement learning and implications for general AI.",
    socialLinks: {
      twitter: "https://twitter.com/sarahjohnson",
      linkedin: "https://linkedin.com/in/sarahjohnson",
      website: "https://sarahjohnson.ai"
    }
  },
  {
    id: "g2",
    name: "Michael Chen",
    imageUrl: "https://randomuser.me/api/portraits/men/2.jpg",
    title: "Founder & CEO",
    company: "GreenTech Solutions",
    email: "michael.chen@example.com",
    bio: "Michael Chen is a serial entrepreneur focused on sustainable technology. His company, GreenTech Solutions, develops innovative approaches to renewable energy storage and distribution.",
    topics: ["Sustainability", "Clean Energy", "Entrepreneurship"],
    tags: ["Startup", "CleanTech"],
    status: "pending",
    notes: "Suggested by listeners. Reach out to schedule a recording session.",
    socialLinks: {
      twitter: "https://twitter.com/michaelchen",
      linkedin: "https://linkedin.com/in/michaelchen",
      website: "https://greentechsolutions.com"
    }
  },
  {
    id: "g3",
    name: "Dr. Emily Rodriguez",
    imageUrl: "https://randomuser.me/api/portraits/women/3.jpg",
    title: "Neuroscientist",
    company: "Brain Research Institute",
    email: "emily.rodriguez@example.com",
    phone: "+1 (555) 987-6543",
    bio: "Dr. Rodriguez studies the intersection of neuroscience and technology, focusing on brain-computer interfaces and neural implants for medical applications.",
    topics: ["Neuroscience", "Brain-Computer Interfaces", "Medical Technology"],
    status: "completed",
    scheduledDate: format(subDays(new Date(), 5), "yyyy-MM-dd'T'HH:mm:ss"),
    recordingUrl: "https://example.com/recordings/emily-rodriguez",
    notes: "Fascinating discussion about the future of neural implants. Need to follow up on the research papers she mentioned.",
    socialLinks: {
      linkedin: "https://linkedin.com/in/emilyrodriguez",
      website: "https://brainresearch.org/team/rodriguez"
    }
  },
  {
    id: "g4",
    name: "James Wilson",
    imageUrl: "https://randomuser.me/api/portraits/men/4.jpg",
    title: "Cybersecurity Expert",
    company: "SecureNet Consulting",
    email: "james.wilson@example.com",
    bio: "James Wilson has over 15 years of experience in cybersecurity, specializing in threat intelligence and security architecture for enterprise organizations.",
    topics: ["Cybersecurity", "Ethical Hacking", "Data Privacy"],
    tags: ["Security", "Tech"],
    status: "published",
    scheduledDate: format(subDays(new Date(), 20), "yyyy-MM-dd'T'HH:mm:ss"),
    recordingUrl: "https://example.com/recordings/james-wilson",
    socialLinks: {
      twitter: "https://twitter.com/jameswilsonsec",
      linkedin: "https://linkedin.com/in/jameswilson",
      website: "https://securenetconsulting.com"
    }
  },
  {
    id: "g5",
    name: "Aisha Patel",
    imageUrl: "https://randomuser.me/api/portraits/women/5.jpg",
    title: "Space Systems Engineer",
    company: "Orbital Dynamics",
    email: "aisha.patel@example.com",
    phone: "+1 (555) 234-5678",
    bio: "Aisha Patel designs propulsion systems for next-generation spacecraft. Her work focuses on sustainable and efficient technologies for long-duration space missions.",
    topics: ["Space Technology", "Aerospace Engineering", "Future of Space Exploration"],
    status: "scheduled",
    scheduledDate: format(addDays(new Date(), 7), "yyyy-MM-dd'T'HH:mm:ss"),
    notes: "Confirmed for next week. Prepare questions about the new propulsion system her team is developing.",
    socialLinks: {
      linkedin: "https://linkedin.com/in/aishapatel",
      twitter: "https://twitter.com/aishainspace"
    }
  }
];

export const episodes: Episode[] = [
  {
    id: "e1",
    title: "The Future of Artificial Intelligence",
    description: "Dr. Sarah Johnson discusses cutting-edge developments in AI research and what the future holds for machine learning technologies.",
    guestId: "g1",
    recordingDate: format(addDays(new Date(), 3), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "scheduled",
    thumbnailUrl: "https://images.unsplash.com/photo-1591453089816-0fbb971b454c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80",
    tags: ["AI", "Technology", "Future"],
    episodeNumber: 15
  },
  {
    id: "e2",
    title: "Sustainable Technology and Green Entrepreneurship",
    description: "Michael Chen shares insights on building sustainable technology companies and the future of clean energy solutions.",
    guestId: "g2",
    recordingDate: format(addDays(new Date(), 10), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "pending",
    thumbnailUrl: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1744&q=80",
    tags: ["Sustainability", "Entrepreneurship", "CleanTech"],
    episodeNumber: 16
  },
  {
    id: "e3",
    title: "Breakthroughs in Brain-Computer Interfaces",
    description: "Dr. Emily Rodriguez explores recent advances in neuroscience and the development of brain-computer interfaces for medical applications.",
    guestId: "g3",
    recordingDate: format(subDays(new Date(), 5), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "completed",
    thumbnailUrl: "https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1780&q=80",
    audioUrl: "https://example.com/episodes/e3.mp3",
    duration: "54:32",
    tags: ["Neuroscience", "Medical Technology", "Research"],
    showNotes: "In this episode, we discussed the latest breakthroughs in neural interface technology and their potential applications in treating neurological disorders.",
    episodeNumber: 14
  },
  {
    id: "e4",
    title: "Modern Cybersecurity Challenges",
    description: "James Wilson discusses the evolving landscape of cybersecurity threats and how organizations can protect their digital assets.",
    guestId: "g4",
    recordingDate: format(subDays(new Date(), 20), "yyyy-MM-dd'T'HH:mm:ss"),
    publishDate: format(subDays(new Date(), 15), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "published",
    thumbnailUrl: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80",
    audioUrl: "https://example.com/episodes/e4.mp3",
    videoUrl: "https://example.com/episodes/e4.mp4",
    duration: "1:02:15",
    showNotes: "In this episode, we explore the latest cybersecurity threats facing organizations today...",
    tags: ["Cybersecurity", "Data Privacy", "Technology"],
    listenCount: 8734,
    episodeNumber: 13
  },
  {
    id: "e5",
    title: "The Next Frontier in Space Exploration",
    description: "Aisha Patel shares insights on next-generation spacecraft propulsion and the future of human space exploration.",
    guestId: "g5",
    recordingDate: format(addDays(new Date(), 7), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "scheduled",
    thumbnailUrl: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1744&q=80",
    tags: ["Space", "Engineering", "Future"],
    episodeNumber: 17
  },
  {
    id: "e6",
    title: "Digital Marketing Strategies for 2023",
    description: "Expert marketer Lisa Johnson shares cutting-edge digital marketing strategies that are working in today's competitive landscape.",
    guestId: "g1",
    recordingDate: format(subDays(new Date(), 40), "yyyy-MM-dd'T'HH:mm:ss"),
    publishDate: format(subDays(new Date(), 35), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "published",
    thumbnailUrl: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1744&q=80",
    audioUrl: "https://example.com/episodes/e6.mp3",
    videoUrl: "https://example.com/episodes/e6.mp4",
    duration: "48:22",
    showNotes: "Lisa shared incredible insights about the changing landscape of social media marketing and how AI is transforming content creation.",
    tags: ["Marketing", "Digital", "Strategy"],
    listenCount: 12453,
    episodeNumber: 12
  },
  {
    id: "e7",
    title: "Mental Health in the Workplace",
    description: "Psychologist Dr. Michael Rivera discusses strategies for maintaining mental health in high-pressure work environments.",
    guestId: "g3",
    recordingDate: format(subDays(new Date(), 55), "yyyy-MM-dd'T'HH:mm:ss"),
    publishDate: format(subDays(new Date(), 50), "yyyy-MM-dd'T'HH:mm:ss"),
    status: "published",
    thumbnailUrl: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80",
    audioUrl: "https://example.com/episodes/e7.mp3",
    duration: "56:10",
    showNotes: "Dr. Rivera provided practical tips for recognizing burnout symptoms and implementing daily stress management techniques.",
    tags: ["Mental Health", "Workplace", "Wellness"],
    listenCount: 15678,
    episodeNumber: 11
  }
];

export const mockStatistics: Statistics = {
  totalGuests: guests.length,
  upcomingEpisodes: episodes.filter(ep => ep.status === 'scheduled' || ep.status === 'pending').length,
  publishedEpisodes: episodes.filter(ep => ep.status === 'published').length,
  reachStats: {
    totalListens: 24750,
    avgListensPerEpisode: 6188,
    growth: 12.5
  }
};
